import { Box, styled } from '@mui/material'
import { isSupported } from 'firebase/messaging'
import { isEqual } from 'lodash'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath, Route, Switch } from 'react-router-dom'

import Copyright from '#/components/Copyright'
import Flex from '#/components/Flex'
import FloatActions from '#/components/FloatActions'
import Layout from '#/components/Layout'
import { PageHeader } from '#/components/PageHeader'
import RevalidateVersion from '#/components/RevalidateVersion'
import { getFirebaseInfo, getFirebaseToken } from '#/store/ducks/auth'
import { withUser } from '#/store/hocs/withUser'
import useCxTracking from '#/store/hooks/tracking'
import { BrowserGuard } from '#/store/services/BrowserGuard'
import { toRoutePath } from '#/store/services/Pages/PagesService'
import { usePages } from '#/store/services/Pages/usePages'
import Fonts from '#/styles/Fonts'
import Colors from '#/styles/Old/Colors'
import lazyRetry from '#/utils/lazyRetry'
import { PlaywrightAlert } from '#/utils/playwrightAlert'
import ws from '#/utils/websocket'

const Wrapper = styled(Flex)(({ ai, theme }) => ({
  alignItems: 'flex-start',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'auto',
  position: 'relative',
  ...(ai !== 'true' && {
    height: 'calc(100vh - 64px)',
    padding: '8px 15px 0',
    [theme.breakpoints.up('lg')]: {
      marginLeft: 240,
    },
  }),
}))
const Content = styled(Box)({
  flex: 1,
  margin: 'auto',
  // Espaçamento para o float da vic não sobrepor o conteúdo
  paddingBottom: '50px',
  width: '100%',
})

const Footer = styled(Box)({
  alignSelf: 'center',
  color: Colors.chipGray,
  fontSize: Fonts.fontSize.SSS,
  marginTop: 'auto',
  padding: 5,
})

// Lazy-loaded components
const Logout = React.lazy(() => lazyRetry(() => import('../pages/Auth/Logout')))

// Guest Routes
// TODO REMOVER TUDO DESSA ROTA EM SETEMBRO
const BemEstar = React.lazy(() => lazyRetry(() => import('../pages/BemEstar')))

// Private Routes
const Access = React.lazy(() => lazyRetry(() => import('../pages/Access')))
const DatabaseRoutes = React.lazy(() => lazyRetry(() => import('../pages/System/Database/routes')))
const CSRoutes = React.lazy(() => lazyRetry(() => import('../pages/System/CS/routes')))
const DataViewRoutes = React.lazy(() => lazyRetry(() => import('../pages/System/DataView/routes')))
const AccountsRoutes = React.lazy(() => lazyRetry(() => import('../pages/System/Accounts/routes')))
const UserRoutes = React.lazy(() => lazyRetry(() => import('../pages/User/routes')))
const FarmRoutes = React.lazy(() => lazyRetry(() => import('../pages/Farm/routes')))
const SystemRoutes = React.lazy(() => lazyRetry(() => import('../pages/System/routes')))

// Fallback Pages
const NotFound = React.lazy(() => lazyRetry(() => import('../pages/Fallback/NotFound')))
const MaintenanceMode = React.lazy(() =>
  lazyRetry(() => import('../pages/Fallback/MaintenanceMode')),
)
const SuspendedAccountMode = React.lazy(() =>
  lazyRetry(() => import('../pages/Fallback/SuspendedAccountMode')),
)

/**
 * PrivateRoute Component
 *   A wrapper for <Route> that redirects to the login
 *   screen if you're not yet authenticated.
 *   and app/api versions.
 * @param {Object} component: <typeof ReactComponent>
 * @param {Object} rest: <typeof ReactComponentProps>
 * @returns {Page} Renders current page component
 */
export const PrivateRoute = ({ component: Component, ...rest }) => {
  useCxTracking()

  const dispatch = useDispatch()
  const { firebaseToken, messagingToken } = useSelector(getFirebaseInfo, isEqual)

  /* Getting the firebase token from the backend and storing it in the redux store. */
  useEffect(() => {
    isSupported().then(hasFirebaseMessagingSupport => {
      if (hasFirebaseMessagingSupport && !firebaseToken && !messagingToken) {
        dispatch(getFirebaseToken())
      }
    })
  }, [firebaseToken, messagingToken])

  // echo websocket connection
  useEffect(() => {
    if (!window.Echo?.socketId()) {
      ws.connect()
    }
  }, [])

  return (
    <Route
      {...rest}
      render={routeProps => {
        const pathname = routeProps?.location?.pathname ?? ''

        const isVicChatRoute = Boolean(
          matchPath(pathname, {
            exact: false,
            path: '/farm/:farm/vic',
          }),
        )

        let layout: React.ReactNode = null
        let footer: React.ReactNode = null
        let floatActions: React.ReactNode = null

        if (!isVicChatRoute) {
          layout = <Layout />
          footer = (
            <Footer>
              <Copyright />
            </Footer>
          )
          floatActions = <FloatActions />
        }

        return (
          <>
            {layout}
            <Wrapper ai={String(isVicChatRoute)}>
              <Content id="app-content">
                <PlaywrightAlert />
                <div id="app-content-top-middle" />
                <PageHeader />
                <Component {...routeProps} />
              </Content>
              {footer}
            </Wrapper>
            {floatActions}
            <RevalidateVersion />
          </>
        )
      }}
    />
  )
}

/**
 * Monta as rotas a partir do `pagesById`: cada item é o retorno de `pages.getById()`, que traz
 * `url`, `id` e a permissão da página junto do `component` passado na chamada. A `url` vira
 * path de react-router pelo `toRoutePath`. Rota cuja página está negada não é registrada.
 */
export const mapRoutes = routes =>
  routes
    .filter(page => page?.enabled)
    .map(({ component, exact, id, protected: isProtected, url }) => {
      const path = toRoutePath(url)

      return isProtected ? (
        <PrivateRoute key={id} component={component} exact={exact} path={path} />
      ) : (
        <Route key={id} component={component} exact={exact} path={path} />
      )
    })

const AppRoutes = () => {
  const pages = usePages()

  const ROUTES = [
    pages.getById('guest-logout', { component: Logout, exact: true }),
    pages.getById('guest-bem-estar-frankanna', { component: BemEstar, exact: true }),
    pages.getById('system-accesses', { component: Access, exact: true, protected: true }),
    pages.getById('guest-not-found', { component: NotFound, protected: true }),
    pages.getById('guest-maintenance', { component: MaintenanceMode }),
    pages.getById('guest-suspended-account', { component: SuspendedAccountMode }),
    {
      component: DataViewRoutes,
      enabled: true,
      id: 'data-view-routes',
      protected: true,
      url: '/data-view',
    },
    {
      component: FarmRoutes,
      enabled: true,
      id: 'farm-routes',
      protected: true,
      url: '/farm/{farm}',
    },
    {
      component: AccountsRoutes,
      enabled: true,
      id: 'accounts-routes',
      protected: true,
      url: '/accounts',
    },
    {
      component: CSRoutes,
      enabled: true,
      id: 'cs-routes',
      protected: true,
      url: '/cs',
    },
    {
      component: DatabaseRoutes,
      enabled: true,
      id: 'database-routes',
      protected: true,
      url: '/database',
    },
    {
      component: UserRoutes,
      enabled: true,
      id: 'user-routes',
      protected: true,
      url: '/user',
    },
    {
      component: SystemRoutes,
      enabled: true,
      id: 'system-routes',
      protected: true,
      url: '/system',
    },
  ]

  return (
    <Switch>
      {mapRoutes(ROUTES)}
      <Route component={NotFound} />
    </Switch>
  )
}

const AppRoutesWithBrowserGuard = () => (
  <BrowserGuard>
    <AppRoutes />
  </BrowserGuard>
)

const AppRoutesWithHOC = withUser(AppRoutesWithBrowserGuard)

export { AppRoutesWithHOC as AppRoutes }
